import type { GetProfilePictureResponseDto } from '../types'

/**
 * Profile picture size options
 */
export type ProfilePictureSize = 'small' | 'medium' | 'large' | 'original'

/**
 * Get the best available profile picture URL based on size preference
 * @param profilePictureData - The profile picture data from API
 * @param preferredSize - The preferred size (defaults to 'large')
 * @returns The best available URL or null if no profile picture
 */
export function getOptimalProfilePictureUrl(
  profilePictureData: GetProfilePictureResponseDto | null | undefined,
  preferredSize: ProfilePictureSize = 'large',
): string | null {
  if (!profilePictureData?.profilePicture) return null

  const { urls, processing } = profilePictureData.profilePicture

  // If still processing thumbnails, always use original URL
  if (processing) {
    return urls.original || null
  }

  // Get the best available URL based on preferred size with fallbacks
  // Always fallback to original first, then other sizes
  switch (preferredSize) {
    case 'large':
      return urls.large || urls.original || urls.medium || urls.small || null
    case 'medium':
      return urls.medium || urls.original || urls.large || urls.small || null
    case 'small':
      return urls.small || urls.original || urls.medium || urls.large || null
    case 'original':
      return urls.original || null
    default:
      return urls.original || null
  }
}

/**
 * Check if profile picture is currently being processed
 * @param profilePictureData - The profile picture data from API
 * @returns Boolean indicating if processing is in progress
 */
export function isProfilePictureProcessing(
  profilePictureData: GetProfilePictureResponseDto | null | undefined,
): boolean {
  return profilePictureData?.profilePicture?.processing || false
}

/**
 * Get profile picture info including URL and processing status
 * @param profilePictureData - The profile picture data from API
 * @param preferredSize - The preferred size (defaults to 'large')
 * @returns Object with URL, processing status, and availability
 */
export function getProfilePictureInfo(
  profilePictureData: GetProfilePictureResponseDto | null | undefined,
  preferredSize: ProfilePictureSize = 'large',
) {
  const url = getOptimalProfilePictureUrl(profilePictureData, preferredSize)
  const processing = isProfilePictureProcessing(profilePictureData)

  return {
    url,
    processing,
    isAvailable: !!url,
    hasOptimizedSizes: !!(
      profilePictureData?.profilePicture?.urls.small ||
      profilePictureData?.profilePicture?.urls.medium ||
      profilePictureData?.profilePicture?.urls.large
    ),
  }
}
